import React, {useCallback, useEffect, useState} from "react"
import {useNavigate, Location} from "react-router-dom"
import CloseIcon from "@mui/icons-material/Close"
import {Box, Button, Typography} from "@mui/material"

import {resolveCustomerPath} from "../utils/resolveCustomerPath"

import {AdvancedSearchContainer} from "./AdvancedSearch/AdvancedSearchContainer"
import {AdvancedSearchEntryLink} from "./AdvancedSearch/AdvancedSearchEntryLink"
import {CustomerResultsTable} from "./CustomerResultsTable"

import {api, CustomerSearchResponse, CustomerSearchResult} from "@indebted/api"
import {Search} from "@indebted/components/Search"
import {Layout} from "@indebted/components/Layout"

function CustomersPage({location}: {location: Location}) {
	const advancedSearchEnabled = import.meta.env.VITE_ENABLE_ADVANCED_SEARCH_UI === "true"
	const params = new URLSearchParams(location.search)
	const query = params.get("q")
	// Allow the panel to be opened via the URL (?advanced=true) so the entry link on the
	// customer/account detail page can route here straight into advanced mode.
	const [showAdvanced, setShowAdvanced] = useState(advancedSearchEnabled && params.get("advanced") === "true")
	// Reported by the advanced-search panel once the client list loads. The
	// narrowing rule below is the authoritative statement of what can anchor a
	// search, so it has to name custom fields wherever they can anchor one —
	// leaving them out reads as "these four only", which is wrong for any client
	// that has them.
	const [customFieldsAvailable, setCustomFieldsAvailable] = useState(false)
	const advancedActive = advancedSearchEnabled && showAdvanced
	const navigate = useNavigate()

	// Close the panel and drop ?advanced=true so the URL no longer reopens advanced mode
	// (e.g. if bookmarked or refreshed). Preserve any other params (like q). `location` is a
	// prop sourced from useLocation() upstream, so it re-renders on navigation and the
	// location.search dep keeps this closure reading the current query string.
	const closeAdvanced = useCallback(() => {
		setShowAdvanced(false)
		const next = new URLSearchParams(location.search)
		next.delete("advanced")
		const qs = next.toString()
		navigate(qs ? `/customers?${qs}` : "/customers", {replace: true})
	}, [navigate, location.search])

	// The link self-gates on the feature flag; pass its wrapper spacing so no empty box
	// renders when the flag is off. Only shown when the panel isn't already open.
	const advancedLink = !showAdvanced ? (
		<AdvancedSearchEntryLink onClick={() => setShowAdvanced(true)} wrapperSx={{px: 3, mt: -2, mb: 1}} />
	) : null

	return (
		<Layout>
			<Box px={3} pt={3} pb={0}>
				<Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
					<Typography variant="h5" fontWeight={600}>
						{advancedActive ? "Advanced Search" : "Search"}
					</Typography>
					{advancedActive && (
						<Button
							size="small"
							startIcon={<CloseIcon />}
							onClick={closeAdvanced}
							sx={{textTransform: "none", flexShrink: 0}}
						>
							Close
						</Button>
					)}
				</Box>
				<Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
					{advancedActive
						? customFieldsAvailable
							? "Combine several details to find the right customer. Enter at least one of last name, phone, email, SSN (last 4), or a custom field value, then add more to narrow the results — searching by SSN alone also needs a date of birth or last name, and a custom field value needs a client."
							: "Combine several details to find the right customer. Enter at least one of last name, phone, email, or SSN (last 4), then add more to narrow the results — searching by SSN alone also needs a date of birth or last name."
						: "Find a customer by one detail: name, account or customer reference, phone, email, or payment code."}
				</Typography>
				{advancedActive && (
					<Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
						Address line 1 and postcode only refine results — they cannot search on their own, so pair them
						with one of the fields above. Address line 1 must match the full line exactly.
					</Typography>
				)}
			</Box>
			{!advancedActive &&
				(query ? (
					<WithQuery query={query} location={location} advancedLink={advancedLink} />
				) : (
					<WithoutQuery advancedLink={advancedLink} />
				))}
			{advancedSearchEnabled && (
				<AdvancedSearchContainer
					hidden={!showAdvanced}
					backSearch={location.search}
					onCustomFieldsAvailable={setCustomFieldsAvailable}
				/>
			)}
		</Layout>
	)
}

function WithoutQuery({advancedLink}: {advancedLink: React.ReactNode}) {
	return (
		<>
			<Search value={""} loading={false} error={""} />
			{advancedLink}
		</>
	)
}

function WithQuery({
	query,
	location,
	advancedLink,
}: {
	query: string
	location: Location
	advancedLink: React.ReactNode
}) {
	const [state, setState] = useState<{customers: CustomerSearchResponse; loading: boolean; error: string}>({
		customers: [],
		loading: false,
		error: "",
	})

	const navigate = useNavigate()

	const navigateToCustomer = useCallback(
		(searchResult: CustomerSearchResult) => {
			setState((prev) => ({...prev, loading: true}))
			api.customer
				.get(searchResult.ID, new Date().getTimezoneOffset())
				.then((customer) => {
					const path = resolveCustomerPath(customer)
					navigate(
						`${path}?q=${encodeURIComponent(query)}&back=/customers${encodeURIComponent(location.search)}`,
					)
				})
				.catch(() => {
					setState((prev) => ({...prev, loading: false}))
					navigate(
						`/customers/${searchResult.ID}?q=${encodeURIComponent(query)}&back=/customers${encodeURIComponent(location.search)}`,
					)
				})
		},
		[query, location, navigate],
	)

	useEffect(() => {
		setState({customers: [], loading: true, error: ""})
		api.customer
			.search(query)
			.then((customers) => {
				setState({customers: customers, loading: false, error: ""})
				if (customers.length === 1) {
					navigateToCustomer(customers[0])
				}
			})
			.catch((error) => {
				setState({customers: [], loading: false, error: error.Message})
			})
	}, [query, navigateToCustomer])

	return (
		<>
			<Search value={query} loading={state.loading} error={state.error} />
			{advancedLink}
			<CustomerResultsTable customers={state.customers} onRowClick={navigateToCustomer} />
		</>
	)
}

export {CustomersPage}
